import type { FC } from 'react'

import { useExpandableText } from '@skytech/utils'
import { mapInternalLocaleToDayjs } from '@skytech/utils/translatedLocale'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import CheckIcon from 'mdi-react/CheckIcon'
import { InView } from 'react-intersection-observer'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import type { SystemMessage as SystemMessageType } from '@/types'

dayjs.extend(relativeTime)

const SystemMessage: FC<
  SystemMessageType & {
    seenMessages?: number[]
    maxTextHeight?: number
    onSeenMessagesSet: (v: number[]) => void
  }
> = ({ id, start, message, header, seenMessages, maxTextHeight = 200, onSeenMessagesSet }) => {
  const { textRef, isTextExpandable } = useExpandableText(maxTextHeight)

  const { locale } = useAppContext()
  const translate = useTranslation()

  const isRead = seenMessages?.includes(id)
  const dayjsLocale = mapInternalLocaleToDayjs(locale)
  const startedAgo = dayjs().locale(dayjsLocale).to(dayjs(start))

  const handleMessageSeen = (isVisible: boolean) => {
    const readSystemMessages = localStorage.getItem('read-system-messages')
    const readSystemMessagesParsed = readSystemMessages ? JSON.parse(readSystemMessages) : undefined

    if (!isVisible) return

    if (!readSystemMessagesParsed) {
      localStorage.setItem('read-system-messages', JSON.stringify([id]))
      onSeenMessagesSet([id])

      return
    }

    if (isRead) return

    localStorage.setItem('read-system-messages', JSON.stringify([...readSystemMessagesParsed, id]))
    onSeenMessagesSet([...readSystemMessagesParsed, id])
  }

  return (
    <div>
      <div className="system-message-header">
        <small>{startedAgo}</small>

        {isRead && (
          <small className="read-info">
            <span role="img" aria-label={translate('checkmark-icon')} className="read-icon">
              <CheckIcon />
            </span>

            <Translation id="read" />
          </small>
        )}
      </div>

      <h3>{header}</h3>

      <InView threshold={1} onChange={handleMessageSeen}>
        <p ref={textRef} className={`message ${isTextExpandable ? 'is-message-expandable' : ''}`}>
          {message}
        </p>
      </InView>
    </div>
  )
}

export default SystemMessage
